import { useState } from "react";
import { Button, Modal, Form, Input, message, Tooltip, Alert } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import { getLineasActaPO, completarDatosActaPO } from "../../../../services/financiero/ingresosPO/entregasPOService";
import { descargarActaPDF } from "../pdf/descargarActaPDF";
import { objetivoPorDefecto } from "../pdf/actaData";

const tiene = (v) => !!String(v ?? "").trim();

export default function DescargarActaButton({ record, userLogin, onDatosGuardados }) {

  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [generando, setGenerando] = useState(false);

  const faltaVereda = !tiene(record.vereda);
  const faltaObjetivo = !tiene(record.objetivo);

  const generar = async (extras = {}) => {
    const lineas = await getLineasActaPO({
      oid_participante: record.oid_participante,
      tipo_entrega: record.tipo_entrega,
      fecha_entrega: record.fecha_entrega
    });
    if (!lineas.length) {
      message.warning("El acta no tiene productos");
      return false;
    }
    await descargarActaPDF(record, lineas, extras);
    message.success("PDF generado");
    return true;
  };

  // Si el acta ya tiene vereda y objetivo se descarga de una vez; solo se
  // piden (una única vez, porque quedan guardados) cuando faltan.
  const handleClick = async () => {
    if (faltaVereda || faltaObjetivo) {
      form.setFieldsValue({
        vereda: record.vereda || "",
        objetivo: record.objetivo || objetivoPorDefecto(record.proyecto)
      });
      setOpen(true);
      return;
    }
    try {
      setGenerando(true);
      await generar();
    } catch (error) {
      console.error(error);
      message.error("Error generando el PDF");
    } finally {
      setGenerando(false);
    }
  };

  const handleGuardarYGenerar = async () => {
    let valores;
    try {
      valores = await form.validateFields();
    } catch {
      return;
    }
    try {
      setGenerando(true);
      const extras = {
        vereda: faltaVereda ? valores.vereda : record.vereda,
        objetivo: faltaObjetivo ? valores.objetivo : record.objetivo
      };
      try {
        await completarDatosActaPO({
          oid_participante: record.oid_participante,
          tipo_entrega: record.tipo_entrega,
          fecha_entrega: record.fecha_entrega,
          vereda: extras.vereda,
          objetivo: extras.objetivo
        }, userLogin);
        onDatosGuardados?.();
      } catch (error) {
        console.error(error);
        message.warning("No se pudieron guardar los datos en el acta; se usarán solo para este PDF.");
      }
      if (await generar(extras)) setOpen(false);
    } catch (error) {
      console.error(error);
      message.error("Error generando el PDF");
    } finally {
      setGenerando(false);
    }
  };

  return (
    <>
      <Tooltip title="Descargar acta en PDF">
        <Button
          size="small"
          icon={<FilePdfOutlined />}
          loading={generando && !open}
          onClick={handleClick}
        />
      </Tooltip>
      <Modal
        open={open}
        title="Completar datos del acta"
        onCancel={() => setOpen(false)}
        onOk={handleGuardarYGenerar}
        okText="Guardar y descargar PDF"
        cancelText="Cancelar"
        confirmLoading={generando}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16, borderRadius: 8 }}
          message={`A esta acta de ${record.nombre_entrega} le falta ${faltaVereda && faltaObjetivo ? "la vereda y el objetivo" : faltaVereda ? "la vereda" : "el objetivo"}.`}
          description="Quedan guardados en el acta y no se volverán a pedir."
        />
        <Form form={form} layout="vertical" autoComplete="off">
          {faltaVereda && (
            <Form.Item
              name="vereda"
              label="Vereda"
              rules={[{ required: true, whitespace: true, message: "Ingrese la vereda" }]}
            >
              <Input maxLength={150} placeholder="Ej: San Clemente" />
            </Form.Item>
          )}
          {faltaObjetivo && (
            <Form.Item
              name="objetivo"
              label="Objetivo del acta"
              tooltip="Se propone uno según el proyecto; puede ajustarlo."
              rules={[{ required: true, whitespace: true, message: "Ingrese el objetivo" }]}
            >
              <Input.TextArea rows={4} maxLength={2000} showCount />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
}
